import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus, LoaderCircle, UserRound } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { fetchProfile, updateProfile } from '../../redux/slices/authSlice';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { classNames } from '../../utils/classNames';

const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';

export const UserProfileForm = () => {
  const { t, formatNumber } = useI18n();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const avatarInputRef = useRef(null);

  const [fullName, setFullName] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      setLoading(true);
      setLoadError('');

      try {
        const profile = await dispatch(fetchProfile()).unwrap();

        if (!cancelled) {
          setFullName(profile.fullName || profile.name || '');
          setAvatarPreviewUrl(profile.avatarUrl || '');
        }
      } catch (requestError) {
        if (!cancelled) {
          setLoadError(requestError?.message || t('profileLoadError'));
          setFullName(user?.fullName || user?.name || '');
          setAvatarPreviewUrl(user?.avatarUrl || '');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load profile once on mount
  }, [dispatch]);

  useEffect(() => {
    if (!avatarFile) {
      return undefined;
    }

    const objectUrl = URL.createObjectURL(avatarFile);
    setAvatarPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [avatarFile]);

  useEffect(() => {
    if (!avatarFile && user?.avatarUrl) {
      setAvatarPreviewUrl(user.avatarUrl);
    }
  }, [avatarFile, user?.avatarUrl]);

  const openAvatarPicker = () => {
    if (loading || saving) {
      return;
    }

    avatarInputRef.current?.click();
  };

  const handleAvatarSelect = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setAvatarFile(file);
    setSaveSuccess(false);
  };

  const clearAvatarSelection = () => {
    setAvatarFile(null);
    setAvatarPreviewUrl(user?.avatarUrl || '');

    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedName = fullName.trim();

    if (!trimmedName) {
      setNameError(t('profileFullNameRequired'));
      return;
    }

    if (saving || loading) {
      return;
    }

    setNameError('');
    setSaveError('');
    setSaveSuccess(false);
    setSaving(true);

    try {
      const profile = await dispatch(
        updateProfile({
          fullName: trimmedName,
          avatarFile
        })
      ).unwrap();

      setFullName(profile.fullName || profile.name || trimmedName);
      setAvatarFile(null);
      setAvatarPreviewUrl(profile.avatarUrl || '');

      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }

      setSaveSuccess(true);
    } catch (requestError) {
      setSaveError(requestError.message || t('profileUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[220px] items-center justify-center rounded-2xl border border-dashed border-border bg-white/40 p-8 dark:bg-slate-950/10">
        <LoaderCircle size={28} className="animate-spin text-accent" aria-hidden="true" />
        <span className="sr-only">{t('loading')}</span>
      </div>
    );
  }

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      {loadError ? (
        <p
          role="alert"
          className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200"
        >
          {loadError}
        </p>
      ) : null}

      <div className="grid gap-5 sm:grid-cols-[minmax(160px,200px)_minmax(0,1fr)] sm:items-start">
        <section className="rounded-2xl border border-border/80 bg-white/75 p-4 dark:bg-slate-950/30">
          <p className="text-sm font-semibold text-text">{t('profileAvatar')}</p>
          <p className="mt-1 text-xs text-muted">{t('profileAvatarHint')}</p>

          <input
            ref={avatarInputRef}
            accept="image/*"
            className="sr-only"
            disabled={saving}
            onChange={handleAvatarSelect}
            type="file"
          />

          <button
            type="button"
            disabled={saving}
            onClick={openAvatarPicker}
            className="group relative mt-4 w-full cursor-pointer overflow-hidden rounded-[1.15rem] border border-border bg-gradient-to-br from-slate-100 via-accent-soft/25 to-teal-100/35 transition hover:border-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 disabled:cursor-not-allowed disabled:opacity-60 dark:from-slate-900 dark:via-accent/10 dark:to-slate-900"
            aria-label={t('profileAvatarChoose')}
          >
            {avatarPreviewUrl ? (
              <img alt="" className="aspect-square w-full object-cover" src={avatarPreviewUrl} />
            ) : (
              <div className="flex aspect-square flex-col items-center justify-center gap-2 text-muted">
                <UserRound size={40} className="text-accent/80" />
                <span className="text-2xl font-bold text-text">{getInitials(fullName)}</span>
              </div>
            )}

            <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-950/0 transition group-hover:bg-slate-950/45 group-focus-visible:bg-slate-950/45">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-slate-950/60 px-3 py-1.5 text-xs font-semibold text-white opacity-0 backdrop-blur-sm transition group-hover:opacity-100 group-focus-visible:opacity-100">
                <ImagePlus size={14} />
                {t('profileAvatarChoose')}
              </span>
            </span>
          </button>

          {avatarFile ? (
            <Button type="button" variant="ghost" className="mt-3 w-full" disabled={saving} onClick={clearAvatarSelection}>
              {t('cancel')}
            </Button>
          ) : null}
        </section>

        <div className="space-y-4">
          <Input
            label={t('profileFullName')}
            placeholder={t('profileFullNamePlaceholder')}
            value={fullName}
            disabled={saving}
            onChange={(event) => {
              setFullName(event.target.value);
              setNameError('');
              setSaveSuccess(false);
            }}
          />
          {nameError ? <p className="text-xs text-rose-600 dark:text-rose-300">{nameError}</p> : null}

          <article className="rounded-2xl border border-border/80 bg-white/75 p-4 dark:bg-slate-950/30">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted">{t('mobileLabel')}</p>
            <p className="mt-2 text-sm font-semibold text-text">
              {user?.mobile ? formatNumber(user.mobile) : '--'}
            </p>
          </article>

          {saveError ? (
            <p className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200">
              {saveError}
            </p>
          ) : null}

          {saveSuccess ? (
            <p
              className={classNames(
                'rounded-2xl border px-4 py-3 text-sm',
                'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200'
              )}
            >
              {t('profileUpdateSuccess')}
            </p>
          ) : null}

          <Button type="submit" className="w-full sm:w-auto" disabled={saving}>
            {saving ? <LoaderCircle size={18} className="animate-spin" /> : null}
            {t('profileSave')}
          </Button>
        </div>
      </div>
    </form>
  );
};
